import React, { useState } from 'react';
import { string, func, arrayOf, shape, number } from 'prop-types';
import classNames from 'classnames';

import { Menu, MenuContent, MenuItem, MenuLabel, IconArrowHead } from '../../../../components';
import css from './SortBy.module.css';

/**
 * Finds and returns the label for a given option key
 *
 * @param {Array} options - Array of option objects
 * @param {string} key - Key to find in options
 * @returns {string} - Label of the found option or key if not found
 */
const getOptionLabel = (options, key) => {
  const option = options.find(o => o.key === key);
  return option ? option.label : key;
};

/**
 * SortBy component - Dropdown menu for sorting options
 */
const SortBy = props => {
  const {
    rootClassName,
    className,
    menuLabelRootClassName,
    urlParam,
    label,
    options,
    initialValue,
    contentPlacementOffset,
    onSelect,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  // Handle menu open/close
  const handleToggleActive = open => {
    setIsOpen(open);
  };

  // Get current menu label text
  const menuLabel = initialValue ? getOptionLabel(options, initialValue) : label;

  // Compose class names
  const classes = classNames(rootClassName || css.root, className);
  const menuLabelClasses = classNames(menuLabelRootClassName || css.menuLabel);

  return (
    <Menu
      className={classes}
      useArrow={false}
      contentPlacementOffset={contentPlacementOffset}
      onToggleActive={handleToggleActive}
      isOpen={isOpen}
    >
      {/* Menu trigger */}
      <MenuLabel className={menuLabelClasses}>
        {menuLabel}
        <IconArrowHead className={css.icon} direction="down" />
      </MenuLabel>

      {/* Menu dropdown content */}
      <MenuContent className={css.menuContent}>
        {options.map(option => {
          // check if this option is selected
          const selected = initialValue === option.key;
          // menu item border class
          const menuItemBorderClass = selected ? css.menuItemBorderSelected : css.menuItemBorder;
          return (
            <MenuItem key={option.key}>
              <button
                className={css.menuItem}
                disabled={option.disabled}
                onClick={() => (selected ? null : onSelect(urlParam, option.key))}
              >
                <span className={menuItemBorderClass} />
                {option.longLabel || option.label}
              </button>
            </MenuItem>
          );
        })}
      </MenuContent>
    </Menu>
  );
};

SortBy.defaultProps = {
  rootClassName: null,
  className: null,
  menuLabelRootClassName: null,
  initialValue: null,
  contentPlacementOffset: 0,
};

SortBy.propTypes = {
  rootClassName: string,
  className: string,
  menuLabelRootClassName: string,
  urlParam: string.isRequired,
  label: string.isRequired,
  onSelect: func.isRequired,
  options: arrayOf(
    shape({
      key: string.isRequired,
      label: string.isRequired,
    })
  ).isRequired,
  initialValue: string,
  contentPlacementOffset: number,
};

export default SortBy;
